import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

/**
 * A small ⓘ icon that shows a tooltip on hover.
 * Uses a portal so the tooltip is never clipped by parent overflow.
 * Dismisses on scroll to prevent stale positioning.
 * Usage: <GlossaryLink section="km" /> or <GlossaryLink section="grid" tooltip="Custom text" />
 */
const GlossaryLink = ({ section, className = '', tooltip }) => {
  const [pos, setPos] = useState(null);

  const content = tooltip || null; // Don't show tooltip if no text provided

  const dismiss = useCallback(() => setPos(null), []);

  // Dismiss tooltip on any scroll event
  useEffect(() => {
    if (!pos) return;
    window.addEventListener('scroll', dismiss, true);
    return () => window.removeEventListener('scroll', dismiss, true);
  }, [pos, dismiss]);

  return (
    <span
      className={`inline-flex items-center justify-center ml-1 opacity-60 hover:opacity-100 transition-opacity ${className}`}
      aria-label="Info"
      onMouseEnter={e => {
        const rect = e.currentTarget.getBoundingClientRect();
        setPos({ x: rect.left + rect.width / 2, y: rect.bottom + 6 });
      }}
      onMouseLeave={dismiss}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-500 cursor-help">
        <circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>
      </svg>
      {pos && content && createPortal(
        <div
          className="fixed z-[99999] max-w-[320px] px-3.5 py-2.5 rounded-lg bg-slate-800 text-white text-[12px] leading-relaxed font-normal shadow-lg whitespace-pre-line text-left pointer-events-none"
          style={{ top: pos.y, left: pos.x, transform: 'translateX(-50%)' }}
        >
          {content}
        </div>,
        document.body
      )}
    </span>
  );
};

export default GlossaryLink;
